import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import { getIndicatorRestitutions } from "../../../../../rest/indicator";
import { currencyFormat } from "../../../../../utils/CurrencyUtils";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import * as XLSX from "xlsx";
import { buildWorkbookNonCompliance } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorNonCompliance";
import KpiStat from "../utils/KpiStat";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorNonCompliance() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await getIndicatorRestitutions();
        const payload = resp?.data || {};
        const s = Array.isArray(payload?.data)
          ? payload.data[0]
          : payload?.data;
        setSummary(s || {});
        setLastUpdate(payload?.lastRefreshedAt || null);
        setRefreshVersion(payload?.refreshVersion || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const asked = summary?.asked ?? 0;
  const payed = summary?.payed ?? 0;
  const total = summary?.total ?? 0;
  const processesIndicated = summary?.processesIndicated ?? 0;

  const CAUSE_I18N_KEYS = {
    FAILURE_TO_CREATE_FULL_TIME_JOB:
      "noncompliance.cause.FAILURE_TO_CREATE_FULL_TIME_JOB",
    FAILURE_TO_INVEST_CREATE_JOBS:
      "noncompliance.cause.FAILURE_TO_INVEST_CREATE_JOBS",
    FAILURE_TO_MAINTAIN_ACTIVITY:
      "noncompliance.cause.FAILURE_TO_MAINTAIN_ACTIVITY",
    FAILURE_TO_MAINTAIN_JOBS_2_YEARS:
      "noncompliance.cause.FAILURE_TO_MAINTAIN_JOBS_2_YEARS",
    UNEMPLOYMENT_BENEFICIARY_LOST_JOB:
      "noncompliance.cause.UNEMPLOYMENT_BENEFICIARY_LOST_JOB",
    FALSE_DECLARATIONS: "noncompliance.cause.FALSE_DECLARATIONS",
    CONTROLLING_SHARE_LOSS: "noncompliance.cause.CONTROLLING_SHARE_LOSS",
    PROMOTER_SHARE_SOLD: "noncompliance.cause.PROMOTER_SHARE_SOLD",
    PERSISTENT_IRREGULARITY: "noncompliance.cause.PERSISTENT_IRREGULARITY",
    MISUSED_FUNDS: "noncompliance.cause.MISUSED_FUNDS",
    UNPAID_WAGES: "noncompliance.cause.UNPAID_WAGES",
    NON_COMPLIANT_WITH_REQUIREMENTS_OBLIGATIONS: "noncompliance.cause.NON_COMPLIANT_WITH_REQUIREMENTS_OBLIGATIONS",
    VERIFIED_IRREGULARITY: "noncompliance.cause.VERIFIED_IRREGULARITY",
    UNDUE_AID_ACCUMULATION: "noncompliance.cause.UNDUE_AID_ACCUMULATION",
  };

  const tCause = (cause) => {
    if (!cause)
      return intl.formatMessage({
        id: "noncompliance.cause.NONE",
        defaultMessage: "Sem motivo",
      });
    const id = CAUSE_I18N_KEYS[cause];
    if (!id) return (cause + "").replaceAll("_", " ");
    return intl.formatMessage({
      id,
      defaultMessage: (cause + "").replaceAll("_", " "),
    });
  };

  const safe = (x) => (Array.isArray(x) ? x : []);
  const block = (enKey, ptKey) => safe(summary?.[enKey] ?? summary?.[ptKey]);

  const comTot = block("comTot", "revogacaoTotalComRestituicao");
  const comPar = block("comPar", "revogacaoParcialComRestituicao");
  const comSem = block("comSem", "semRevogacaoComRestituicao");
  const semTot = block("semTot", "revogacaoTotalSemRestituicao");
  const semPar = block("semPar", "revogacaoParcialSemRestituicao");
  const semSem = block("semSem", "semRevogacaoSemRestituicao");

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookNonCompliance();
      XLSX.writeFile(wb, "indicadores_incumprimentos.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Incumprimentos] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const SectionHeader = ({ title }) => (
    <div className="mb-2 mt-4">
      <span className="text-primary fw-semibold">{title}</span>
    </div>
  );

  const MatrixCell = ({ title, rows, showValue }) => {
    const allItems = safe(rows);
    const bucketTotal = allItems.find((r) => r.cause === "_BUCKET_TOTAL");
    const items = allItems.filter((r) => r.cause !== "_BUCKET_TOTAL");
    const totalCount = bucketTotal ? (bucketTotal.count || 0) : items.reduce((s, r) => s + (r.count || 0), 0);
    const totalValue = bucketTotal ? Number(bucketTotal.askedSum || 0) : items.reduce((s, r) => s + Number(r.askedSum || 0), 0);

    return (
      <div className="container-fluid card border shadow-sm h-100">
        <div className="card-body">
          <div className="mb-1 fw-semibold">{title}</div>
          <div className="mb-3">
            <span>
              Total processos: <strong>{totalCount}</strong>
            </span>
            {showValue && (
              <span className="ms-4">
                Valor: <strong>{currencyFormat(totalValue)}</strong>
              </span>
            )}
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-borderless mb-0">
              <thead>
                <tr>
                  <th className="fw-normal">Motivo</th>
                  <th className="fw-normal text-end">Nº Processos</th>
                  {showValue && (
                    <th className="fw-normal text-end">Valor a Restituir</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td className="text-muted" colSpan={showValue ? 3 : 2}>
                      Sem registos
                    </td>
                  </tr>
                )}
                {items.map((r, i) => (
                  <tr key={i}>
                    <td>{tCause(r.cause)}</td>
                    <td className="text-end">{r.count ?? 0}</td>
                    {showValue && (
                      <td className="text-end">
                        {currencyFormat(r.askedSum ?? 0)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const matrix2x3 = [
    {
      rowTitle: "Com Restituição",
      cols: [
        { title: "Revogação Total", rows: comTot, showValue: true },
        { title: "Revogação Parcial", rows: comPar, showValue: true },
        { title: "Sem Revogação", rows: comSem, showValue: true },
      ],
    },
    {
      rowTitle: "Sem Restituição",
      cols: [
        { title: "Revogação Total", rows: semTot, showValue: false },
        { title: "Revogação Parcial", rows: semPar, showValue: false },
        { title: "Sem Revogação", rows: semSem, showValue: false },
      ],
    },
  ];

  const renderMatrix = (matrix) => (
    <>
      {matrix.map((row, rIdx) => (
        <div key={`r-${rIdx}`} className="mb-3">
          <SectionHeader title={row.rowTitle} />
          <Row className="g-3">
            {row.cols.map((c, cIdx) => (
              <Col key={`c-${rIdx}-${cIdx}`} md={6} lg={4}>
                <MatrixCell
                  title={c.title}
                  rows={c.rows}
                  showValue={c.showValue}
                />
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </>
  );

  return (
    <div ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicators.restitutions"
              defaultMessage="Incumprimentos"
            />
          </h4>
        </Col>
        <Col className="text-end">
          <Button
            variant="link"
            className="p-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </Button>
        </Col>
      </Row>

      <Row className="g-3 mb-2">
        <Col md={3}>
          <KpiStat label="Total Incumprimentos" value={total} />
        </Col>
        <Col md={3}>
          <KpiStat label="Total Processos" value={processesIndicated} />
        </Col>
        <Col md={3}>
          <KpiStat label="Restituições Pedidas" value={currencyFormat(asked)} />
        </Col>
        <Col md={3}>
          <KpiStat label="Restituições Pagas" value={currencyFormat(payed)} />
        </Col>
      </Row>

      <div className="text-muted small mb-2 mt-3">
        Um processo pode apresentar mais do que um motivo de incumprimento.
      </div>

      {renderMatrix(matrix2x3)}

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_incumprimentos.xlsx",
          },
          {
            onClick: () => exportPdf("indicadores_incumprimentos.pdf"),
            filename: "indicadores_incumprimentos.pdf",
          },
        ]}
      />
    </div>
  );
}